import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { del, patch, post } from "@/lib/api";
import type { Contact } from "@/lib/types";
import { isTouch } from "@/lib/utils";

/** contact === "new" creates, a Contact edits, null is closed. */
export function ContactDialog({ companyId, contact, onClose }: { companyId: number; contact: Contact | "new" | null; onClose: () => void }) {
  const qc = useQueryClient();
  const editing = contact !== null && contact !== "new" ? contact : null;
  const [f, setF] = useState({ name: "", role: "", email: "", phone: "", notes: "", primary: false, receives: false });
  useEffect(() => {
    setF(editing
      ? { name: editing.name, role: editing.role ?? "", email: editing.email ?? "", phone: editing.phone ?? "", notes: editing.notes ?? "", primary: editing.is_primary === 1, receives: editing.receives_invoices === 1 }
      : { name: "", role: "", email: "", phone: "", notes: "", primary: false, receives: false });
  }, [contact]);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const done = (msg: string) => {
    qc.invalidateQueries({ queryKey: ["company", companyId] });
    qc.invalidateQueries({ queryKey: ["companies"] });
    toast.success(msg);
    onClose();
  };
  const body = { name: f.name.trim(), role: f.role || null, email: f.email || null, phone: f.phone || null, notes: f.notes || null, primary: f.primary, receivesInvoices: f.receives };
  const save = useMutation({
    mutationFn: () => (editing ? patch<Contact>(`/api/contacts/${editing.id}`, body) : post<Contact>(`/api/companies/${companyId}/contacts`, body)),
    onSuccess: () => done(editing ? "Kontakt gespeichert" : "Kontakt angelegt"),
    onError: (e) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: () => del<{ deleted: boolean; archived: boolean }>(`/api/contacts/${editing!.id}`),
    onSuccess: (r) => done(r.archived ? "Kontakt archiviert (Notizen verweisen darauf)" : "Kontakt gelöscht"),
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open={contact !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{editing ? "Kontakt bearbeiten" : "Kontakt hinzufügen"}</DialogTitle></DialogHeader>
        <form className="contents" onSubmit={(e) => { e.preventDefault(); if (body.name && !save.isPending) save.mutate(); }}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="cn">Name</Label><Input id="cn" autoFocus={!isTouch} autoComplete="off" value={f.name} onChange={set("name")} /></div>
            <div className="space-y-1.5"><Label htmlFor="cr">Rolle</Label><Input id="cr" placeholder="z. B. Marketing" value={f.role} onChange={set("role")} /></div>
            <div className="space-y-1.5"><Label htmlFor="cp">Telefon</Label><Input id="cp" type="tel" value={f.phone} onChange={set("phone")} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="ce">E-Mail</Label><Input id="ce" type="email" spellCheck={false} value={f.email} onChange={set("email")} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="cno">Notizen</Label><Textarea id="cno" rows={2} value={f.notes} onChange={set("notes")} /></div>
            <label className="flex cursor-pointer items-center gap-2 text-sm sm:col-span-2">
              <Checkbox checked={f.primary} onCheckedChange={(v) => setF({ ...f, primary: !!v })} />Hauptkontakt
            </label>
            <label className="flex cursor-pointer items-start gap-2 text-sm sm:col-span-2">
              <Checkbox className="mt-0.5" checked={f.receives} onCheckedChange={(v) => setF({ ...f, receives: !!v })} />
              <span>
                Bekommt die Rechnungen
                <span className="block text-[0.8125rem] text-muted-foreground">Rechnungen und Zahlungserinnerungen gehen an diese Adresse. Pro Firma nur ein Kontakt.</span>
              </span>
            </label>
          </div>
          <DialogFooter className="sm:justify-between">
            {editing
              ? <Button type="button" variant="ghost" className="text-destructive hover:text-destructive" disabled={remove.isPending}
                  onClick={() => confirm(`${editing.name} entfernen?`) && remove.mutate()}>Entfernen</Button>
              : <span />}
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>Abbrechen</Button>
              <Button type="submit" disabled={!body.name || save.isPending}>Speichern</Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
